import { AsYouType, parsePhoneNumberFromString, validatePhoneNumberLength } from "libphonenumber-js";

// US phone numbers. The API stores them as E.164 (+18655551234), the format Twilio needs.
export class PhoneNumber {
    // Whether it's a valid US number, in any common format ("(865) 555-1234", "865.555.1234", "+18655551234")
    static isValidUS(value) {
        const parsed = parsePhoneNumberFromString(String(value ?? ''), 'US');
        return !!parsed && parsed.isValid() && parsed.country === 'US';
    }

    // Why a number isn't a valid US number, in plain words, or null if it's fine (or empty)
    static problem(value) {
        if (!value || String(value).trim() === '') return null;
        if (PhoneNumber.isValidUS(value)) return null;

        const lengthProblem = validatePhoneNumberLength(String(value), 'US');
        if (lengthProblem === 'NOT_A_NUMBER') return "it isn't a phone number";
        if (lengthProblem === 'TOO_SHORT') return 'it has too few digits';
        if (lengthProblem === 'TOO_LONG') return 'it has too many digits';

        const parsed = parsePhoneNumberFromString(String(value), 'US');
        if (parsed && parsed.isValid() && parsed.country !== 'US') return "it isn't a US number";

        return "it isn't a valid US number";
    }

    // Why a *saved* number needs re-entering, or null. Besides being invalid, a valid number saved in an old format
    // ("865-555-1234") can't be texted either: texting needs it saved as +18655551234. Re-saving fixes that.
    static savedProblem(value) {
        const problem = PhoneNumber.problem(value);
        if (problem) return problem;

        if (!value || String(value).trim() === '') return null;

        const parsed = parsePhoneNumberFromString(String(value), 'US');
        return parsed.number === String(value) ? null : "it's saved in an old format";
    }

    // How to show it: "(865) 555-1234" when valid, otherwise exactly what's stored
    static toDisplay(value) {
        if (!value) return '';

        const parsed = parsePhoneNumberFromString(String(value), 'US');
        return parsed && parsed.isValid() && parsed.country === 'US' ? parsed.formatNational() : String(value);
    }

    // Formats what's typed so far, e.g. "8655551" -> "(865) 555-1"
    static formatAsYouType(value) {
        return new AsYouType('US').input(String(value ?? ''));
    }
}
